import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchEmulatorUnits,
  createEmulatorUnit,
  deleteEmulatorUnit,
  emulatorAction,
  type EmulatorUnit,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Play, Pause, Refresh, Zap } from "@/components/icons";
import { statusFilterValue } from "@/lib/unit-helpers";
import { useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

const STATE_COLORS: Record<string, "success" | "warning" | "info" | "secondary" | "error"> = {
  Standby: "secondary",
  Connected: "info",
  Ready: "warning",
  Charging: "success",
  Blocked: "error",
};

function UnitCard({ unit, onRefresh }: { unit: EmulatorUnit; onRefresh: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [amps, setAmps] = useState("32");

  const act = useMutation({
    mutationFn: (params: { action: string; extra?: Record<string, unknown> }) =>
      emulatorAction(unit.unit_id, params.action, params.extra),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["emulator-units"] }),
  });

  const del = useMutation({
    mutationFn: () => deleteEmulatorUnit(unit.unit_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["emulator-units"] }),
  });

  const doAction = (action: string, extra?: Record<string, unknown>) =>
    act.mutate({ action, extra });

  const powerW = unit.voltage * unit.current;

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-mono">{unit.unit_id}</CardTitle>
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${unit.running ? "text-emerald-600" : "text-zinc-400"}`}>
              <span className={`size-2 rounded-full ${unit.running ? "bg-emerald-500 animate-pulse" : "bg-zinc-300"}`} />
              {unit.running ? t("operator.ops.emulator.running") : t("operator.ops.emulator.stopped")}
            </span>
            <Badge variant={STATE_COLORS[unit.state_name] ?? "secondary"}>
              {t("operator.units.status_option", { status: statusFilterValue(unit.state_name), raw: unit.state_name })}
            </Badge>
          </div>
          <div className="flex gap-1">
            {unit.running ? (
              <Button size="sm" variant="outline" onClick={() => doAction("stop")} title={t("operator.ops.emulator.stop_telemetry")}>
                <Pause className="size-4" />
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => doAction("start")} title={t("operator.ops.emulator.start_telemetry")}>
                <Play className="size-4" />
              </Button>
            )}
            <Button size="sm" variant="destructive" onClick={() => del.mutate()} title={t("operator.ops.emulator.delete_unit")}>
              <Trash2 className="size-4" />
            </Button>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-4 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">{t("operator.metric.voltage")}</span>
            <div className="font-mono">{unit.voltage.toFixed(1)} V</div>
          </div>
          <div>
            <span className="text-muted-foreground">{t("operator.metric.current")}</span>
            <div className="font-mono">{unit.current.toFixed(1)} A</div>
          </div>
          <div>
            <span className="text-muted-foreground">{t("operator.metric.power")}</span>
            <div className="font-mono">{(powerW / 1000).toFixed(2)} kW</div>
          </div>
          <div>
            <span className="text-muted-foreground">{t("operator.ops.emulator.temperature")}</span>
            <div className="font-mono">{unit.temperature} °C</div>
          </div>
          <div>
            <span className="text-muted-foreground">{t("operator.ops.emulator.energy")}</span>
            <div className="font-mono">{unit.session_energy} Wh</div>
          </div>
          <div>
            <span className="text-muted-foreground">{t("operator.ops.emulator.soc")}</span>
            <div className="font-mono">{unit.battery_soc}%</div>
          </div>
          <div>
            <span className="text-muted-foreground">{t("operator.ops.emulator.online_limit")}</span>
            <div className="font-mono">{unit.online_limit} A</div>
          </div>
          <div>
            <span className="text-muted-foreground">{t("operator.ops.emulator.session")}</span>
            <div className="font-mono">#{unit.session_num}</div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" variant="outline" onClick={() => doAction("plug_in")}
            disabled={unit.state !== 0}>
            {t("operator.ops.emulator.plug_in")}
          </Button>
          <div className="flex gap-1 items-center">
            <Input className="w-16 h-8 text-xs" type="number" value={amps}
              onChange={(e) => setAmps(e.target.value)} min={0} max={unit.unit_rating} />
            <Button size="sm" variant="default" onClick={() => doAction("start_charging", { amps: Number(amps) })}
              disabled={unit.state === 0 || unit.state === 2}>
              <Zap className="size-3 mr-1" /> {t("operator.ops.emulator.charge")}
            </Button>
          </div>
          <Button size="sm" variant="outline" onClick={() => doAction("stop_charging")}
            disabled={unit.state !== 2}>
            {t("operator.ops.emulator.stop")}
          </Button>
          <Button size="sm" variant="outline" onClick={() => doAction("car_full")}
            disabled={unit.state !== 2}>
            {t("operator.ops.emulator.car_full")}
          </Button>
          <Button size="sm" variant="outline" onClick={() => doAction("unplug")}
            disabled={unit.state === 0}>
            {t("operator.ops.emulator.unplug")}
          </Button>
        </div>

        {/* Last server response */}
        {unit.last_response && (
          <div className="text-xs text-muted-foreground font-mono truncate" title={unit.last_response}>
            {t("operator.ops.emulator.last_response", { response: unit.last_response })}
          </div>
        )}

        {/* Recent log */}
        {unit.log.length > 0 && (
          <details className="text-xs">
            <summary className="text-muted-foreground cursor-pointer">{t("operator.ops.emulator.log", { count: unit.log.length })}</summary>
            <div className="mt-1 max-h-32 overflow-auto bg-muted/50 rounded p-2 font-mono space-y-0.5">
              {unit.log.slice().reverse().map((e, i) => (
                <div key={i}><span className="text-muted-foreground">{e.ts.split("T")[1]}</span> {e.msg}</div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

export function EmulatorPage() {
  const t = useT();
  const qc = useQueryClient();
  const [newId, setNewId] = useState("3737300001");
  const [newInterval, setNewInterval] = useState("10");
  const [newRating, setNewRating] = useState("40");

  const { data, isLoading } = useQuery({
    queryKey: ["emulator-units"],
    queryFn: fetchEmulatorUnits,
    refetchInterval: 5_000, // WS push handles instant updates; polling is fallback
  });

  const create = useMutation({
    mutationFn: () =>
      createEmulatorUnit({
        unit_id: newId.trim(),
        interval: Number(newInterval),
        unit_rating: Number(newRating),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["emulator-units"] });
      // Increment ID for next unit
      const num = parseInt(newId, 10);
      if (!isNaN(num)) setNewId(String(num + 1));
    },
  });

  const units = data?.units ?? [];

  return (
    <div className="space-y-6 pt-[86px]">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t("operator.ops.emulator.title")}</h1>
        <Button variant="outline" size="sm" onClick={() => qc.invalidateQueries({ queryKey: ["emulator-units"] })}>
          <Refresh className="size-4 mr-1" /> {t("common.action.refresh")}
        </Button>
      </div>

      {/* Create new unit */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">{t("operator.units.column_unit_id")}</Label>
              <Input className="w-36" value={newId} onChange={(e) => setNewId(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">{t("operator.ops.emulator.interval")}</Label>
              <Input className="w-20" type="number" value={newInterval}
                onChange={(e) => setNewInterval(e.target.value)} min={1} max={120} />
            </div>
            <div>
              <Label className="text-xs">{t("operator.ops.emulator.rating")}</Label>
              <Input className="w-20" type="number" value={newRating}
                onChange={(e) => setNewRating(e.target.value)} min={6} max={80} />
            </div>
            <Button onClick={() => create.mutate()} disabled={!newId.trim() || create.isPending}>
              <Plus className="size-4 mr-1" /> {t("operator.ops.emulator.add")}
            </Button>
          </div>
          {create.isError && (
            <p className="text-sm text-destructive mt-2">{errorMessage(t, create.error, { fallback: "operator.ops.emulator.create_failed" })}</p>
          )}
        </CardContent>
      </Card>

      {/* Unit list */}
      {isLoading ? (
        <div className="text-muted-foreground">{t("common.state.loading")}</div>
      ) : units.length === 0 ? (
        <div className="text-muted-foreground text-center py-12">
          {t("operator.ops.emulator.empty")}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {units.map((u) => (
            <UnitCard key={u.unit_id} unit={u} onRefresh={() => qc.invalidateQueries({ queryKey: ["emulator-units"] })} />
          ))}
        </div>
      )}
    </div>
  );
}
